import React from 'react';
import {
  useDispatch,
  useSelector,
} from 'react-redux';
import { defineMessages, useIntl } from 'react-intl';
import {
  Formik,
  Form,
  FieldArray,
  useField,
} from 'formik';
import * as actions from 'hellospa/page/prep-and-send/data/actions';
import * as deepIntegrationActions from 'hellospa/page/prep-and-send/data/actions/deep-integration';
import { trackHeapCustomEvent } from 'common/utils/heap';

import * as selectors from 'hellospa/page/prep-and-send/data/selectors';
import * as types from 'hellospa/page/prep-and-send/data/types';
import {
  ConnectFormikToStepper,
  BannerErrorMessage,
  AutoSave,
} from 'hellospa/components/prep-and-send/formik-utils';
import { PageHeader, SectionHeader } from '../header';
import styles from './index.scss';
import messages from './intl';
import Overlay from '../../deep-integration/quota-overlay';
import * as Yup from 'yup';
import { MeOnlyBtn, AddRecipientBtn, AddMeBtn, BulkSendButton } from './buttons';
import RecipientOrder from './RecipientOrder';
import RecipientRows from './RecipientRows';
import RecipientReassignment from './RecipientReassignment';
import QESToggle from './QESToggle';

/**
 * I had to turn off enableReinitialize for autosave, but that broke bulk send.
 * This component was needed to synchronize signerFile from Redux with signerFile in Formik
 */
function SyncFormikValue({ name, value }: { name: string, value: unknown }) {
  const [fieldProps, , helpers] = useField(name);

  React.useEffect(() => {
    if (fieldProps.value !== value) {
      helpers.setValue(value);
    }
  }, [helpers, fieldProps.value, value]);

  return null;
}

const Recipient: types.WorkflowComponent = ({ onSaveActions, formRef }) => {
  const intl = useIntl();
  const dispatch = useDispatch();
  const workflow = useSelector(selectors.getWorkflow);
  const allowTemplatePreview = useSelector(selectors.allowTemplatePreview);
  const showTemplatePreview = allowTemplatePreview
      && workflow.tags.includes(types.WorkflowTags.SignatureRequestTemplate)
      && !workflow.tags.includes(types.WorkflowTags.SignatureRequestDocument);
  const isDeepIntegration = useSelector(selectors.isDeepIntegration);
  const recipientReassignment = useSelector(selectors.isRecipientReassignmentEnabled);
  const recipientOrder = useSelector(selectors.isRecipientOrderEnabled);
  const addRecipientAction = useSelector(selectors.getRecipientGenerator);
  const isBulkSend = useSelector(selectors.isBulkSend);
  let recipients = useSelector(selectors.getRecipientsForRequest);
  const signerFile = useSelector(selectors.getBulkSendData).signerFile;
  const isSendingTemplate = useSelector(selectors.isSendingTemplate);
  const isPaidAccount = useSelector(selectors.isPaidAccount);
  const numDocsLeft = useSelector(selectors.numDocsLeft);
  const isTemplateRequest = useSelector(selectors.isTemplateRequest);
  const qes = useSelector(selectors.getQES);

  // Show or hide section header depending on if there are options
  const allowRecipientReassignment = useSelector(selectors.allowRecipientReassignment);
  const allowRecipientOrder = useSelector(selectors.allowRecipientOrder);
  const allowQES = useSelector(selectors.allowQES);

  let canContinue = true;
  if (isDeepIntegration && !isPaidAccount && numDocsLeft === 0) {
    canContinue = false;
  }

  const baseSchema: Yup.ObjectSchema<InitialValues> | null = useSelector(
    selectors.getRecipientValidationSchema,
  );

  const schema = React.useMemo(() => {
    if (baseSchema) {
      return baseSchema.clone().shape({
        canContinue: Yup.bool().oneOf([true], intl.formatMessage(messages.canNotContinue)),
      });
    }
    return baseSchema;
  }, [baseSchema, intl]);

  const defaultRecipients = React.useMemo(() => {
    if (addRecipientAction) {
      return [addRecipientAction()];
    }

    return null;
  }, [addRecipientAction]);

  type InitialValues = {
    recipients: typeof recipients,
    recipientReassignment: typeof recipientReassignment,
    recipientOrder: typeof recipientOrder,
    qes: boolean,
    signerFile: typeof signerFile,
    canContinue: typeof canContinue,
  };

  const normalizeValues = React.useCallback((values: InitialValues) => {

    const normalizedValues: InitialValues = schema ? schema.cast(values) : values;

    if (isSendingTemplate) {
      const recipients = values.recipients.map((recipient) => {
        const normalizedRecipient = normalizedValues.recipients.find(
          (n) => n.id === recipient.id,
        );

        // If a normalizedRecipient can't be found it should be because it was
        // left empty and is an optional role.
        return normalizedRecipient || recipient;
      });

      return {
        ...values,
        recipients,
      };
    }

    return normalizedValues;

  }, [isSendingTemplate, schema]);

  const saveToRedux = React.useCallback(async (v: InitialValues) => {
    const values = normalizeValues(v);
    await dispatch(actions.resetRecipients(values.recipients));

    // don't update the settings when sending templates because
    // these settings are part of the template and cannot be
    // changed for the specific request
    if (!isSendingTemplate) {
      await dispatch(actions.setSettings({
        recipientReassignment: values.recipientReassignment,
        recipientOrder: values.recipientOrder,
        qes: values.qes,
      }));
    }
  }, [dispatch, isSendingTemplate, normalizeValues]);

  const submit = React.useCallback(async (values: InitialValues) => {
    const total = values.recipients.reduce((total, recipient) => {
      if (recipient.type === types.RecipientTypes.Signer
        || recipient.type === types.RecipientTypes.Role) {
        return total + recipient.attachments.length;
      }
      return total;
    }, 0);
    trackHeapCustomEvent('Added Signers', { 'Signer Count': values.recipients.length });
    trackHeapCustomEvent('Added Attachments', { 'Attachment Count': total });
    await saveToRedux(values);
    if (showTemplatePreview && !process.env.IS_STORYBOOK) {
      // Preview data (editorFields: { fields, rules, pages }) are not included
      // in use-template, so it needs to be fetched before moving to the review
      // page.
      // I considered trying to do this as you leave the documents page, but the
      // workflow may not be known before leaving that page. That means I can't
      // use the same showTemplatePreview logic as this file has.
      await dispatch(actions.refreshPreviewData());
    }
    await dispatch(actions.workflowOnSave(onSaveActions));
  }, [dispatch, onSaveActions, saveToRedux, showTemplatePreview]);

  // mimicking componentDidMount
  React.useEffect(() => {
    if (isTemplateRequest) {
      trackHeapCustomEvent('template_add_roles');
    } else {
      trackHeapCustomEvent('sign_or_send_add_signers');
    }
  }, [isTemplateRequest]);
  /**
   * For deep-integration users, an external file should be auto converted (from their dbx folder)
   * whereas for HS, a user must manually upload a file. Therefore, kick off a file loader / polling
   * function.
   */
  React.useEffect(() => {
    if (isDeepIntegration) {
      trackHeapCustomEvent('deep_integration_initial_loading');
      dispatch(deepIntegrationActions.convertDeepIntegrationFile());
    }
  }, [dispatch, isDeepIntegration]);


  if (!recipients.length && !isBulkSend) {
    if (defaultRecipients) {
      recipients = defaultRecipients;
    } else {
      return null;
    }
  }

  if (!schema) {
    return null;
  }

  const initialValues: InitialValues = {
    recipients,
    recipientReassignment,
    recipientOrder,
    qes,
    signerFile,
    canContinue,
  };

  /**
   * This function bypasses Formik's submit and just moves to the next page. An
   * earlier implementation used Formik to set the recipients to an empty array,
   * but in addition to looking weird to have all recipients disappear it
   * doesn't pass validation.
   */
  const handleMeOnly = async () => {
    return submit({
      recipients: [],
      recipientReassignment: false,
      recipientOrder: false,
      qes: false,
      signerFile: null,
      canContinue,
    });
  };


  let sectionHeader = messages.headerAddSigners;
  if (isBulkSend) {
    sectionHeader = messages.headerUploadCSV;
  } else if (isTemplateRequest) {
    sectionHeader = messages.headerAddSignerRoles;
  }

  return (
    <div data-qa-ref="prep-and-send-recipients">
      {isDeepIntegration && !isPaidAccount && numDocsLeft === 0 && (
        <Overlay />
      )}
      <PageHeader text={intl.formatMessage(messages.headerSignersPage)} />
      <Formik
        initialValues={initialValues}
        onSubmit={async (values) => submit(values)}
        validateOnChange={false}
        validationSchema={schema}
        validateOnMount={true}
        enableReinitialize={
          // This needs to be false for <AutoSave to work
          false
        }
        innerRef={types.useFormikRef(formRef)}
        // eslint-disable-next-line react/no-children-prop
        children={({ values, errors }) => (

          <Form>
            <div className={styles.sectionHeaderRow}>
              <div>
                <SectionHeader text={intl.formatMessage(sectionHeader)} />
              </div>
              <hr />
              <MeOnlyBtn onMeOnly={handleMeOnly} recipients={values.recipients} />
            </div>
            <ConnectFormikToStepper />
            <AutoSave
              values={values}
              errors={errors}
              saveToRedux={saveToRedux}
              reduxSaveAction={actions.save} />
            <SyncFormikValue name="signerFile" value={signerFile} />

            <BannerErrorMessage name="recipients" />
            <BannerErrorMessage name="signerFile.error" requireTouched={false} />
            <BulkSendButton signerFile={values.signerFile} />

            {!isBulkSend && (<FieldArray name="recipients" render={(arrayHelpers) => (
              <React.Fragment>
                <RecipientRows helper={arrayHelpers} />

                <div className={styles.addSignerContainer}>
                  <div>
                    <AddRecipientBtn helper={arrayHelpers} />
                  </div>
                  <div>
                    <AddMeBtn helper={arrayHelpers} />
                  </div>
                </div>
              </React.Fragment>
            )} />)}

            {
              (allowRecipientReassignment
                || allowQES
                || (allowRecipientOrder && values.recipients.length > 1)
              )
              && <div id="recipient-signer-settings-header" className={styles.ssHeader}> Signer Settings </div>
            }
            <div id="recipient-signer-settings">
              <RecipientReassignment />
              <RecipientOrder />
              <QESToggle />
            </div>
          </Form>
        )} />
    </div>
  );
};

Recipient.getText = (workflow) => {
  const msg = defineMessages({
    title: {
      id: '',
      description: 'title of page, also used in Stepper tab',
      defaultMessage: 'Add signers',
    },
    titleRoles: {
      id: '',
      description: 'title of page, also used in Stepper tab',
      defaultMessage: 'Add roles',
    },
    next: {
      id: '',
      description: 'text in next button',
      defaultMessage: 'Next',
    },
  });

  if (workflow.tags.includes(types.WorkflowTags.CreateTemplate)
    || workflow.tags.includes(types.WorkflowTags.CreateTemplateLink)
  ) {
    return {
      ...msg,
      title: msg.titleRoles,
    };
  }

  return msg;
};

export default Recipient;
